import { api, apiErrorMessage } from '@bemor/web-kit';
import {
    DateTime,
    FileUpload,
    FormSheet,
    PageHeader,
    RowActions,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { RiskFeed } from '@/api/types';
import { useOne, useWrite } from '@/lib/queries';

const PATH = '/staff/risk/feeds';

function UploadSheet({
    feed,
    onClose,
}: {
    feed: RiskFeed;
    onClose: () => void;
}) {
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<unknown>(null);
    const write = useWrite(async () => {
        const body = new FormData();
        if (file) body.append('file', file);
        return (await api().post(`${PATH}/${feed.name}/upload`, body)).data;
    }, [PATH]);
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title={`Upload ${feed.name}`}
            submitLabel="Upload and load"
            busy={write.isPending}
            submitDisabled={file === null}
            dirty={file !== null}
            error={error}
            data-testid="risk-feed-upload"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: setError,
                });
            }}
        >
            <p className="text-fg-2 m-0 text-sm">
                A hand-fetched copy of {feed.source_url}, loaded with the same
                parser as the scheduled refresh.
            </p>
            <FileUpload
                id="feed-file"
                label="File"
                accept={[
                    'text/plain',
                    'text/csv',
                    'application/json',
                    'application/xml',
                    'text/xml',
                ]}
                maxBytes={200 * 1024 * 1024}
                value={file}
                onChange={setFile}
            />
        </FormSheet>
    );
}

/** BM-125 decision 24 (A6): the reference-data feeds and their health; a refresh now or a hand-fetched upload. */
export function RiskFeedsPage() {
    const feeds = useOne<RiskFeed[]>(PATH);
    const [uploading, setUploading] = useState<RiskFeed | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const refresh = useWrite(
        async (name: string) =>
            (
                await api().post<{
                    ok: boolean;
                    rows: number;
                    error: string | null;
                    skipped: boolean;
                }>(`${PATH}/${name}/refresh`)
            ).data,
        [PATH],
    );
    return (
        <div>
            <PageHeader
                title="Risk feeds"
                meta="Tor exits, cloud ranges, disposable domains, IP geography and the OFAC lists: each refreshed on its schedule; stale after a day without success."
            />
            {message ? (
                <p className="text-sm" data-testid="feed-message">
                    {message}
                </p>
            ) : null}
            <div className="overflow-x-auto">
                <Table data-testid="risk-feeds">
                    <TableHeader>
                        <TableRow>
                            <TableHead>Feed</TableHead>
                            <TableHead>Schedule</TableHead>
                            <TableHead>Rows</TableHead>
                            <TableHead>Last success</TableHead>
                            <TableHead>Error</TableHead>
                            <TableHead>Version</TableHead>
                            <TableHead />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {(feeds.data?.data ?? []).map((f) => (
                            <TableRow
                                key={f.name}
                                data-testid={`feed-${f.name}`}
                            >
                                <TableCell className="font-mono text-xs">
                                    {f.name}
                                </TableCell>
                                <TableCell>{f.schedule}</TableCell>
                                <TableCell className="tabular-nums">
                                    {f.rows}
                                </TableCell>
                                <TableCell>
                                    {f.last_success_at ? (
                                        <DateTime value={f.last_success_at} />
                                    ) : (
                                        'never'
                                    )}
                                </TableCell>
                                <TableCell className="text-destructive max-w-64 truncate">
                                    {f.last_error ?? ''}
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                    {f.version ?? '—'}
                                    {f.uploaded_by ? ` (upload)` : ''}
                                </TableCell>
                                <TableCell>
                                    <RowActions
                                        name={f.name}
                                        items={[
                                            {
                                                label: 'Refresh now',
                                                disabled: busy === f.name,
                                                onSelect: () => {
                                                    setBusy(f.name);
                                                    refresh.mutate(f.name, {
                                                        onSuccess: (r) => {
                                                            setBusy(null);
                                                            setMessage(
                                                                r.ok
                                                                    ? `${f.name}: ${r.skipped ? 'unchanged' : `${r.rows} rows loaded`}`
                                                                    : `${f.name}: ${r.error}`,
                                                            );
                                                        },
                                                        onError: (e) => {
                                                            setBusy(null);
                                                            setMessage(
                                                                apiErrorMessage(
                                                                    e,
                                                                ) ??
                                                                    'Refresh failed',
                                                            );
                                                        },
                                                    });
                                                },
                                            },
                                            {
                                                label: 'Upload file',
                                                onSelect: () => setUploading(f),
                                            },
                                        ]}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {uploading ? (
                <UploadSheet
                    feed={uploading}
                    onClose={() => setUploading(null)}
                />
            ) : null}
        </div>
    );
}
